import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faChevronDown,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import CustomSelect from "../CustomSelect";
import DateRangePicker from "../date-range-picker";
import { useRangePicker } from "../../store/use-range-picker-model";
import SegmentedControl from "../segmented-control";

type IChaseType = "Sales" | "Renewals" | "all";
type IProductType = "Standard" | "Non-Standard" | "all";

interface IChaseFilterBar {
  type: IChaseType;
  setType: (value: IChaseType) => void;
  productType: IProductType;
  setProductType: (value: IProductType) => void;
  providers: Record<string, any>[];
  pLoading: boolean;
  insurerId: number | null;
  setInsurerId: (value: number | null) => void;
  setProviderSearch: (value: string) => void;
  dateRange: Record<string, any>;
  setDateRange: (value: Record<string, any>) => void;
}

const TYPE_OPTIONS: { label: string; value: IChaseType }[] = [
  { label: "All", value: "all" },
  { label: "Sales", value: "Sales" },
  { label: "Renewals", value: "Renewals" },
];

const PRODUCT_OPTIONS: { label: string; value: IProductType }[] = [
  { label: "All", value: "all" },
  { label: "Standard", value: "Standard" },
  { label: "Non-Standard", value: "Non-Standard" },
];

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const fieldClass =
  "h-9 rounded-lg bg-input-bg border border-transparent text-[13px] font-medium text-text-primary hover:border-indigo-400/40 transition-all duration-200 cursor-pointer";

const ChaseFilterBar: React.FC<IChaseFilterBar> = ({
  type,
  setType,
  productType,
  setProductType,
  providers,
  pLoading,
  insurerId,
  setInsurerId,
  setProviderSearch,
  dateRange,
  setDateRange,
}) => {
  const { onToggleRangePicker } = useRangePicker();
  const hasFilters =
    type !== "all" ||
    productType !== "all" ||
    insurerId !== null ||
    !!dateRange?.startDate;

  // ---------------------------------------- [ CLEAR ALL FILTERS ] ---------------------------
  const HandleClearFilters = () => {
    setType("all");
    setProductType("all");
    setInsurerId(null);
    setDateRange({});
  };

  return (
    <div className="relative z-20 mt-5 mr-1 smallTablet:mr-0 bg-surface border border-edge rounded-2xl shadow-card px-4 py-3 flex items-center gap-x-5 gap-y-3 flex-wrap animate-fade-in">
      <SegmentedControl
        label="Policy"
        options={TYPE_OPTIONS}
        value={type}
        onChange={setType}
      />

      <div className="h-6 w-px bg-edge smallTablet:hidden" />

      <SegmentedControl
        label="Department"
        options={PRODUCT_OPTIONS}
        value={productType}
        onChange={setProductType}
      />

      <div className="ml-auto flex items-center gap-2.5 flex-wrap smallTablet:ml-0 smallTablet:w-full">
        <CustomSelect
          inputClass="!h-9 !w-[220px] smallTablet:!w-full !rounded-lg !bg-input-bg !border-transparent !pl-3 !pr-9 !text-[13px] !font-medium !text-text-primary hover:!border-indigo-400/40 cursor-pointer"
          comboboxType="select"
          placeholder="All insurers"
          options={providers}
          labelProperty="label"
          valueProperty="name"
          hideOnFocus
          isLoading={pLoading}
          onInputChange={(e) => setProviderSearch(e)}
          onSingleSelectChange={(e) => setInsurerId(e?.name ?? null)}
          value={providers.find((prov) => prov.name === insurerId)}
        />

        <DateRangePicker onChange={(e) => setDateRange(e)}>
          <div
            onClick={onToggleRangePicker}
            className={`${fieldClass} flex items-center gap-2 px-3 select-none whitespace-nowrap`}
          >
            <FontAwesomeIcon icon={faCalendar} className="text-[12px] text-light-gray" />
            {dateRange?.startDate
              ? `${formatDate(dateRange.startDate)}${
                  dateRange?.endDate ? ` — ${formatDate(dateRange.endDate)}` : ""
                }`
              : "All dates"}
            <FontAwesomeIcon
              icon={faChevronDown}
              className="ml-1 text-[10px] text-light-gray"
            />
          </div>
        </DateRangePicker>

        {hasFilters && (
          <button
            type="button"
            onClick={HandleClearFilters}
            className="h-9 px-3 flex items-center gap-1.5 rounded-lg text-[13px] font-medium text-light-gray hover:text-text-primary hover:bg-overlay-hover transition-colors whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faXmark} className="text-[12px]" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default ChaseFilterBar;
